const CARD_LIMIT = 4;
const FETCH_LIMIT = 10;

class CartDrawerSuggestions extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude-ids'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {number} */
  #idleId = 0;
  /** Kept off the DOM: section morphs strip attributes the server does not render. */
  #loaded = false;
  #loading = false;
  #scheduled = false;
  #requestId = 0;

  connectedCallback() {
    if (this.dataset.mode !== 'complementary') return;
    this.#watchTrack();
    this.#schedule();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue == null || oldValue === newValue) return;
    if (!this.isConnected || this.dataset.mode !== 'complementary') return;

    this.#requestId++;
    this.#abortController?.abort();
    this.#cancelScheduled();
    this.#loading = false;
    this.#loaded = false;
    this.hidden = true;
    this.querySelector('[data-suggestions-track]')?.replaceChildren();
    this.#schedule();
  }

  disconnectedCallback() {
    this.#requestId++;
    this.#cancelScheduled();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController?.abort();
    this.#abortController = null;
    this.#loading = false;
  }

  #cancelScheduled() {
    if (this.#idleId) {
      if (typeof window.requestIdleCallback === 'function' && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.#idleId);
      } else {
        clearTimeout(this.#idleId);
      }
      this.#idleId = 0;
    }
    this.#scheduled = false;
  }

  #schedule() {
    if (!this.isConnected || this.#loaded || this.#loading || this.#scheduled) return;
    this.#scheduled = true;

    const start = () => {
      this.#idleId = 0;
      this.#scheduled = false;
      if (!this.isConnected) return;
      this.#load();
    };

    if (typeof window.requestIdleCallback === 'function') {
      this.#idleId = window.requestIdleCallback(start, { timeout: 1500 });
    } else {
      this.#idleId = window.setTimeout(start, 200);
    }
  }

  /**
   * Reloads only when an external update empties a track we already filled.
   * The callback must not write child nodes, or it would schedule itself forever.
   */
  #watchTrack() {
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      if (this.#loading || this.#scheduled) return;
      const track = this.querySelector('[data-suggestions-track]');
      if (!(track instanceof HTMLElement) || track.childElementCount > 0) return;
      if (!this.#loaded) return;
      this.#loaded = false;
      this.#schedule();
    });
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  async #load() {
    const productId = this.dataset.productId;
    const track = this.querySelector('[data-suggestions-track]');
    const template = this.querySelector('template[data-suggestion-card]');
    if (!productId || !(track instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    const requestId = ++this.#requestId;
    this.#loading = true;
    this.#loaded = true;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${FETCH_LIMIT}&intent=complementary`;
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (requestId !== this.#requestId || !this.isConnected) return;

      if (!response.ok) {
        this.#showFallback(track);
        return;
      }

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      const excluded = this.#excludedIds();
      const cards = products
        .filter((product) => !excluded.has(String(product.id)) && product.available !== false)
        .slice(0, CARD_LIMIT);

      const fragment = document.createDocumentFragment();
      for (const product of cards) {
        const card = this.#fillCard(template, product);
        if (card) fragment.append(card);
      }

      if (requestId !== this.#requestId || !this.isConnected) return;

      if (fragment.childElementCount) {
        this.#replaceTrack(track, fragment);
        this.hidden = false;
        return;
      }

      this.#showFallback(track);
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== this.#requestId || !this.isConnected) return;
      this.#showFallback(track);
    } finally {
      if (requestId === this.#requestId) this.#loading = false;
    }
  }

  /**
   * @param {HTMLElement} track
   */
  #showFallback(track) {
    const fallback = this.querySelector('template[data-fallback-cards]');
    if (!(fallback instanceof HTMLTemplateElement)) {
      this.hidden = true;
      return;
    }

    // Section morphs do not update template content, so the server-side exclusion can be stale.
    const excluded = this.#excludedIds();
    const fragment = document.createDocumentFragment();
    for (const node of fallback.content.children) {
      if (fragment.childElementCount >= CARD_LIMIT) break;
      const id = node.querySelector('product-form-component')?.getAttribute('data-product-id');
      if (id && excluded.has(id)) continue;
      fragment.append(node.cloneNode(true));
    }

    if (!this.isConnected || !fragment.childElementCount) {
      this.hidden = true;
      return;
    }

    this.#replaceTrack(track, fragment);
    this.hidden = false;
  }

  /** @returns {Set<string>} */
  #excludedIds() {
    const ids = (this.dataset.excludeIds || '').split(',').map((id) => id.trim()).filter(Boolean);
    if (this.dataset.productId) ids.push(this.dataset.productId);
    return new Set(ids);
  }

  /**
   * @param {HTMLElement} track
   * @param {DocumentFragment} fragment
   */
  #replaceTrack(track, fragment) {
    this.#observer?.disconnect();
    try {
      track.replaceChildren(fragment);
    } finally {
      if (this.isConnected) this.#watchTrack();
    }
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {Record<string, any>} product
   * @returns {Element | null}
   */
  #fillCard(template, product) {
    const card = template.content.firstElementChild?.cloneNode(true);
    if (!(card instanceof HTMLElement)) return null;

    const productUrl = this.#productUrl(product);
    const imageUrl = this.#imageUrl(product);
    const variant = this.#firstAvailableVariant(product);
    if (!productUrl || !variant) return null;

    const choose = (product.variants?.length || 0) > 1;
    const links = card.querySelectorAll('a');
    for (const link of links) {
      link.href = productUrl;
    }

    const title = card.querySelector('.cart-drawer-suggestions__title');
    if (title) title.textContent = product.title || '';

    const price = card.querySelector('.cart-drawer-suggestions__price');
    if (price) price.textContent = this.#formatMoney(product.price);

    const image = card.querySelector('img');
    if (image instanceof HTMLImageElement) {
      image.alt = product.title || '';
      if (imageUrl) image.src = imageUrl;
      else image.remove();
    }

    const quickAdd = card.querySelector('quick-add-component');
    if (quickAdd instanceof HTMLElement) {
      quickAdd.dataset.quickAddButton = choose ? 'choose' : 'add';
      quickAdd.dataset.productOptionsCount = String(product.options?.length || (choose ? 2 : 1));
      quickAdd.dataset.productTitle = product.title || '';
    }

    const formComponent = card.querySelector('product-form-component');
    if (formComponent instanceof HTMLElement) {
      formComponent.dataset.productId = String(product.id);
      if (this.dataset.sectionId) formComponent.dataset.sectionId = this.dataset.sectionId;
    }

    const variantInput = card.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = String(variant.id);

    return card;
  }

  /**
   * @param {Record<string, any>} product
   */
  #productUrl(product) {
    if (typeof product.url === 'string' && product.url) return product.url;
    const root = window.Shopify?.routes?.root || '/';
    if (typeof product.handle === 'string' && product.handle) return `${root}products/${product.handle}`;
    return '';
  }

  /**
   * @param {Record<string, any>} product
   */
  #imageUrl(product) {
    const image = product.featured_image;
    let src = '';
    if (typeof image === 'string') src = image;
    else if (image && typeof image.src === 'string') src = image.src;
    else if (image && typeof image.url === 'string') src = image.url;

    if (!src) return '';
    if (src.startsWith('//')) src = `https:${src}`;

    try {
      const url = new URL(src, window.location.origin);
      if (!url.searchParams.has('width')) url.searchParams.set('width', '112');
      return url.toString();
    } catch {
      return src;
    }
  }

  /**
   * @param {Record<string, any>} product
   */
  #firstAvailableVariant(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.find((variant) => variant.available !== false) || variants[0] || null;
  }

  /**
   * @param {number | string} cents
   */
  #formatMoney(cents) {
    const format = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    const amount = (value / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(value / 100));
    const withThousands = (number, separator) => number.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    const amountComma = withThousands(amount.replace('.', ','), '.');
    const amountSpace = withThousands(amount.replace('.', ','), ' ');
    const amountApostrophe = withThousands(amount, "'");

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withThousands(amountNoDecimals, '.'))
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, withThousands(amountNoDecimals, ' '))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withThousands(amountNoDecimals, ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, amountSpace)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountApostrophe)
      .replace(/\{\{\s*amount\s*\}\}/g, withThousands(amount, ','));
  }
}

if (!customElements.get('cart-drawer-suggestions')) {
  customElements.define('cart-drawer-suggestions', CartDrawerSuggestions);
}
